import axios from "axios";
import React, { useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { toast } from "react-toastify";
import SpinnerGif from "../../assets/images/Spinner.gif";
import { _imagesURL, adminBaseURL } from "../../constants/API";
import { Language } from "../../App";
import { Link } from "react-router-dom";
import { PROFILE } from "../../constants/RoutesUrl";
import ImageComponent from "../common/ImageComponent";
import Avatar from "../../assets/images/icon/placeholder_Awatar.png";
import { authorize } from "../../shared/authorize";
import debounce from "lodash/debounce";

const PurchasedUsersModal = ({ isOpen, toggle, courseId }) => {
  const { lang } = useContext(Language);
  const [users, setUsers] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Refs for managing state
  const abortControllerRef = useRef(null);
  const loadingRef = useRef(false);
  const currentCourseIdRef = useRef(null);
  const mountedRef = useRef(true);

  // Cache per course
  const cacheRef = useRef(new Map());

  // Reset component state when modal opens or courseId changes
  const resetState = useCallback(() => {
    setUsers([]);
    setPage(1);
    setHasMore(true);
    setError(null);
    setLoading(false);
    setIsInitialized(false);
    loadingRef.current = false;

    // Clear cache for this course
    if (courseId) {
      cacheRef.current.delete(courseId);
    }

    // Create fresh abort controller
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
  }, [courseId]);

  const fetchPurchasedUsers = useCallback(
    async (pageNum, isRetry = false) => {
      // Validate inputs
      if (!courseId) {
        setError('Course ID is required');
        return;
      }

      // Prevent concurrent requests
      if (loadingRef.current && !isRetry) {
        return;
      }

      // Don't fetch if component is unmounted
      if (!mountedRef.current) {
        return;
      }

      // Check if we have more pages to load
      if (!hasMore && pageNum > 1) {
        return;
      }
      // Check cache first
      const courseCache = cacheRef.current.get(courseId);
      if (courseCache && courseCache.has(pageNum)) {
        const cachedData = courseCache.get(pageNum);

        if (pageNum === 1) {
          setUsers(cachedData.users);
        } else {
          setUsers(prev => [...prev, ...cachedData.users]);
        }

        setPage(pageNum + 1);
        setHasMore(cachedData.hasMore);
        setIsInitialized(true);
        return;
      }

      // Set loading state
      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        // Ensure we have fresh abort controller
        if (!abortControllerRef.current || abortControllerRef.current.signal.aborted) {
          abortControllerRef.current = new AbortController();
        }
        const response = await axios.get(
          `${adminBaseURL}user/course/users/purchased/${courseId}?page=${pageNum}`,
          {
            ...authorize(),
            signal: abortControllerRef.current.signal,
            timeout: 10000, // 10 second timeout
          }
        );

        // Check if component is still mounted
        if (!mountedRef.current) {
          return;
        }
        const responseData = response.data.data;
        const fetchedUsers = responseData.data || [];
        const currentPage = responseData.current_page;
        const lastPage = responseData.last_page;
        const hasMorePages = currentPage < lastPage;

        // Update cache
        if (!cacheRef.current.has(courseId)) {
          cacheRef.current.set(courseId, new Map());
        }
        const courseCache = cacheRef.current.get(courseId);
        courseCache.set(pageNum, {
          users: fetchedUsers,
          hasMore: hasMorePages
        });

        // Update state
        if (pageNum === 1) {
          setUsers(fetchedUsers);
        } else {
          setUsers(prev => {
            // Prevent duplicates
            const existingIds = new Set(prev.map(u => u.id));
            const newUsers = fetchedUsers.filter(u => !existingIds.has(u.id));
            return [...prev, ...newUsers];
          });
        }

        setPage(pageNum + 1);
        setHasMore(hasMorePages);
        setIsInitialized(true);

      } catch (error) {
        if (!mountedRef.current) {
          return;
        }

        if (error.name === "AbortError") {
          return;
        }

        const errorMessage = error.response?.data?.message ||
          error.message ||
          lang?.error_fetching_users ||
          "Failed to fetch purchased users";

        setError(errorMessage);
        setHasMore(false);
        setIsInitialized(true); // Set initialized even on error

        if (pageNum === 1) {
          toast.error(errorMessage);
        }
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [courseId, hasMore, lang]
  );

  // Debounced load more to prevent rapid successive calls
  const debouncedLoadMore = useMemo(
    () =>
      debounce((pageNum) => {
        if (loadingRef.current) {
          return;
        }
        fetchPurchasedUsers(pageNum);
      }, 500), // Increased debounce time
    [fetchPurchasedUsers]
  );

  // Handle modal open/close and courseId changes
  useEffect(() => {
    if (isOpen && courseId) {
      // Only reset if courseId changed or modal just opened
      if (currentCourseIdRef.current !== courseId) {
        currentCourseIdRef.current = courseId;
        resetState();

        // Use requestAnimationFrame to ensure DOM is ready and state is reset
        requestAnimationFrame(() => {
          if (mountedRef.current && isOpen && courseId) {
            fetchPurchasedUsers(1);
          }
        });
      } else if (!isInitialized && !loadingRef.current) {
        // If same courseId but not initialized and not loading, fetch data
        fetchPurchasedUsers(1);
      }
    } else if (!isOpen) {
      // Clean up when modal closes
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      currentCourseIdRef.current = null;
    }
  }, [isOpen, courseId, resetState, fetchPurchasedUsers, isInitialized]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Clear all cache on unmount
      cacheRef.current.clear();
    };
  }, []);

  // Handle retry
  const handleRetry = useCallback(() => {
    resetState();
    setTimeout(() => {
      if (mountedRef.current) {
        fetchPurchasedUsers(1, true);
      }
    }, 100);
  }, [resetState, fetchPurchasedUsers]);

  // Load more handler for InfiniteScroll
  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore && isInitialized) {
      debouncedLoadMore(page);
    }
  }, [loading, hasMore, isInitialized, page, debouncedLoadMore]);

  const userList = useMemo(
    () =>
      users.map((user, index) => (
        <div key={`${user.id}-${index}`}>
          <div className="d-flex p-2 justify-content-between manue-hover rounded">
            <Link
              to={`${PROFILE}/${user.id}`}
              className="d-flex gap-2 w-100 text-dark"
              aria-label={`View profile of ${user.name || "user"}`}
            >
              <div
                className="bg-white dropdown-avatar-shadow img-small-rounded align-self-center rounded"
              >
                <ImageComponent
                  src={user?.image && user.image !== "null" ? _imagesURL + user.image : Avatar}
                  compClassName="img-fluid rounded"
                  loader_height="3rem"
                  loader_width="3rem"
                  alt={`${user.name || "user"}'s avatar`}
                />
              </div>
              <div className="align-self-center">
                <div className="fs-17 roboto-bold text-dark text-ellips-110px">
                  {user.name || "Unknown User"}
                </div>
                <p className="mb-0 fs-12 roboto-regular text-ellips-110px">
                  {user.subcategory || user.user_name || "N/A"}
                </p>
              </div>
            </Link>
          </div>
          {index !== users.length - 1 && <hr className="m-0 custom-opacity-6" />}
        </div>
      )),
    [users]
  );

  return (
    <Modal
      scrollable
      centered
      zIndex={9}
      isOpen={isOpen}
      toggle={toggle}
      backdrop="static"
    >
      <ModalHeader toggle={toggle}>
        {lang?.users_who_purchased || "Users Who Purchased"}
      </ModalHeader>
      <ModalBody className="scroll-hide min-height-400">
        {!isInitialized && loading ? (
          <div className="w-100 text-center py-4">
            <img src={SpinnerGif} alt={lang?.loading || "Loading..."} width={30} height={30} />
            <div className="mt-2 text-muted">{lang?.loading || "Loading..."}</div>
          </div>
        ) : error ? (
          <div className="text-center text-danger py-4">
            <div className="mb-3">{error}</div>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleRetry}
              disabled={loading}
            >
              {loading ? (lang?.loading || "Loading...") : (lang?.retry || "Retry")}
            </button>
          </div>
        ) : (
          <InfiniteScroll
            pageStart={0}
            loadMore={handleLoadMore}
            hasMore={hasMore && !loading}
            loader={
              <div className="w-100 text-center py-2" key="loader">
                <img src={SpinnerGif} alt={lang?.loading || "Loading..."} width={30} height={30} />
              </div>
            }
            useWindow={false}
            threshold={500}
            initialLoad={false}
          >
            {users.length > 0 ? (
              userList
            ) : (
              !loading && (
                <div className="text-center text-muted py-4">
                  {lang?.no_users_found || "No users found"}
                </div>
              )
            )}
          </InfiniteScroll>
        )}
      </ModalBody>
    </Modal>
  );
};

export default React.memo(PurchasedUsersModal);